<template>
  <div>
  <header class="fixed top-0 left-0 right-0 z-10 antialiased font-sans flex flex-col px-4 transition-colors duration-300 lg:z-50"
    :class="[headerClass, {'summer-header': promotionEnabled && showHeaderBackground, 'summer-header-transparent': promotionEnabled && !showHeaderBackground}]">
    <div v-if="showBackground" class="absolute top-0 left-0 w-full h-full backdrop-blur-sm z-[-1]"></div>

    <div class="w-[1200px] mx-auto flex justify-between items-center">
      <!-- logo -->
      <div class="px-4 py-3 sm:p-0">
        <a class="inline-block" href="/">
          <img class="h-8" src="@/assets/logo.png" alt="SparkPROXY">
        </a>
      </div>

      <nav class="flex-auto flex items-center justify-evenly mx-auto">
        <!--pricing-->
        <FlyMenuItem class="px-[19.5px]" @hoverChanged="handleHoverChange">
          <template v-slot:menu>
            <span class="inline-block">{{ $t("header.pricing") }}</span>
            <span v-if="promotionEnabled" class="header-menu-promo">{{ promotionLimitedOffer }}</span>
            <span v-else class="inline-block mx-1 px-1 rounded-full bg-transparent ring-1 ring-pink-500 text-pink-500 text-sm">
              <span class="lottie-header-icon"></span>
              <span class="discount-activity-txt">{{ $t("header.lowest_price") }}</span>
            </span>
          </template>
          
          <div class="flex columns-3 w-[1200px] mx-auto bg-white py-3 space-x-10">
            <div v-for="plan in plans" class="flex flex-col basis-1/3">
              <div class="pb-3 text-gray-500">{{ plan.name }}</div>
              <div class="flex flex-col space-y-5">
                  <a class="inline-flex items-center space-x-3 p-2 hover:bg-gray-100 hover:rounded-md" v-for="v in plan.child" :href="v.url" @click="handleClick">
                    <div class="aspect-auto w-10">
                        <img :src="getImg(v.img)" alt="">
                    </div>
                    <div class="inline-flex items-center justify-between flex-auto">
                        <div class="inline-flex flex-col">
                            <span class="font-bold text-black">{{ v.name }}</span>
                            <span class="text-sm text-gray-500">{{ v.tips }}</span>
                        </div>
                        <div v-if="v.activity" class="w-[173px]">
                            <div class="inline-box pt-[23px] -mt-[21px] leading-8 text-sm bg-[url('@/assets/index/img/activities/header_residential_proxies.svg')] bg-top bg-no-repeat text-center text-white">Starts from {{v.salePrice}}{{ v.saleUnit }}</div>
                        </div>
                        <div v-else-if="v.salePrice !== undefined" class="inline-flex items-center justify-end gap-[8px] text-right text-sm text-gray-500">
                            <span>{{ $t("header.price_starting") }}</span>
                            <div class="inline-box">
                              <span class="font-bold text-black">{{ v.salePrice }}</span>
                              <span>{{ v.saleUnit }}</span>
                            </div>
                        </div>
                    </div>
                  </a>
              </div>
            </div>
        </div>
        </FlyMenuItem>

        <!--regions-->
        <FlyMenuItem class="px-[19.5px]" @hoverChanged="handleHoverChange">
          <template v-slot:menu>
            <span class="inline-block">{{ $t("header.regions") }}</span>
          </template>
          <div class="flex w-[1200px] mx-auto bg-white py-3 space-x-10">
            <div class="flex flex-col w-full">
              <div class="pb-3 text-gray-500 divide-y">{{ $t("header.regions") }}</div>
              <div class="grid grid-cols-5 gap-4">
                
                <a v-for="region in regions" :key="region.area"
                  :href="`/staticisp/${region.area}.html`" 
                  class="inline-flex items-center space-x-3 w-full p-2 hover:bg-gray-100 hover:rounded-md">
                  <div class="aspect-auto w-10">
                    <img :src="getRegionImg(region.area)" :alt="region.name">
                  </div>
                  <div class="inline-flex items-center justify-between flex-auto">
                    <div class="inline-flex flex-col">
                      <span class="font-bold text-black">{{ region.name }}</span>
                      <span class="text-sm text-gray-500">{{ region.stocks | formatNumber }} IPs</span>
                    </div>
                  </div>
                </a>

                <!--more-->
                <a class="inline-flex items-center space-x-3 w-full p-2 hover:bg-gray-100 hover:rounded-md" href="/staticisp/">
                  <div class="aspect-auto w-10">
                      <img src="@/assets/index/img/index/img_more.png" alt="">
                  </div>
                  <div class="inline-flex items-center justify-between flex-auto">
                      <div class="inline-flex flex-col">
                          <span class="font-bold text-black">{{ $t("header.more_region") }}</span>
                          <span class="text-sm text-gray-500">{{ $t("header.all_regions") }}</span>
                      </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </FlyMenuItem>

        <!--Help Center-->
        <FlyMenuItem class="px-[19.5px]" @hoverChanged="handleHoverChange">
          <template v-slot:menu>
            <span class="inline-block">{{ $t("header.help_center") }}</span>
          </template>
          
          <div class="flex w-[1200px] mx-auto bg-white py-3 space-x-10">
            <div class="flex flex-col">
              <div class="pb-3 text-gray-500 divide-y">{{ $t("header.help_center") }}</div>
              <div class="flex flex-row space-x-5">
                <a class="inline-flex items-center space-x-3 w-full p-2 hover:bg-gray-100 hover:rounded-md" href="/faq/" onclick="winhref('/faq/','_blank','')">
                  <div class="aspect-auto w-10">
                      <img src="@/assets/index/img/index/img_faq.png" alt="">
                  </div>
                  <div class="inline-flex items-center justify-between flex-auto">
                      <div class="inline-flex flex-col">
                          <span class="font-bold text-black">{{ $t("header.faq.caption") }}</span>
                          <span class="text-sm text-gray-500">{{ $t("header.faq.intro") }}</span>
                      </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </FlyMenuItem>

        <button
          type="button"
          class="agent-entry px-[19.5px]"
          @click="openAgentDialog"
        >
          {{ $t('header.agent.entry') }}
        </button>
      </nav>

      <div class="flex items-center">
        <div class="language-switch" aria-label="Language switch">
          <button
            type="button"
            :class="['language-switch__item', {'is-active': currentLanguage === 'zh-cn'}]"
            @click="changeLanguage('zh-cn')"
          >中文</button>
          <span class="language-switch__divider">/</span>
          <button
            type="button"
            :class="['language-switch__item', {'is-active': currentLanguage === 'en'}]"
            @click="changeLanguage('en')"
          >EN</button>
        </div>
        
        <AccountDropdown id="registerBtn" class="sm:block sm:ml-6 z-10" :showLogin="showLogin" :showSignup="showSignup" :hasBackground="showBackground || isHovered"/>
      </div>
    </div>
  </header>

  <!--quick group-->
  <div v-if="!simple" class="quick-group fixed bottom-[119px] right-[25px]">
    <div class="btn-list">
      <a v-if="showGoTop" href="javascript:void(0);" @click="scrollToTop" rel="nofollow" class="relative flex justify-center items-center mt-[36px] w-[50px] h-[50px] rounded-full border-0 bg-[#fff]">
        <img v-show="atTop" src="@/assets/index/img/index/right_top_no_icon.svg" alt="" class="no">
        <img v-show="!atTop" src="@/assets/index/img/index/right_top_act_icon.svg" alt="" class="hov">
      </a>
      <a href="#"
        class="group relative flex justify-center items-center mt-[36px] w-[50px] h-[50px] rounded-full border-0 text-white shadow-[0px_4px_20px_#3c457633]" 
        style="background: linear-gradient(138deg, #2e6062, #69a971);"
        @mouseenter="isQrCardVisible = true" @mouseleave="isQrCardVisible = false">
        <img src="@/assets/index/img/index/right_wechat_icon.svg" alt="">
        <transition name="fade">
          <qr-card class="qr-card-panel" v-if="isQrCardVisible"/>
        </transition>
      </a>
      <a href="javascript:void(0);" @click.prevent="openWechatQr"
        class="group relative flex justify-center items-center mt-[36px] w-[50px] h-[50px] rounded-full border-0 text-white shadow-[0px_4px_20px_#3c457633]" 
        style="background: linear-gradient(138deg, #2e6062, #69a971);">
        <img src="@/assets/index/img/index/right_email_icon.svg" alt="">
      </a>
      <a :href="agentTelegramUrl" target="_blank"
        class="group relative flex justify-center items-center mt-[36px] w-[50px] h-[50px] rounded-full border-0 text-white shadow-[0px_4px_20px_#3c457633]" 
        style="background: linear-gradient(138deg, #2e6062, #69a971);">
        <img src="@/assets/index/img/index/right_telegram_icon.svg" alt="">
        <div class="absolute bottom-0 left-[50%] transform translate-x-[-50%] translate-y-[100%] w-auto text-[12px] text-[#3A9AE7] leading-[20px] hidden group-hover:inline-block">Telegram</div>
      </a>
    </div>
  </div>

  <el-dialog
    :visible.sync="isAgentDialogVisible"
    custom-class="agent-dialog"
    width="420px"
    :show-close="true"
    append-to-body
  >
    <div class="agent-dialog__body">
      <div class="agent-dialog__icon">🔝</div>
      <h3>{{ $t('header.agent.title') }}</h3>
      <p>{{ $t('header.agent.desc') }}</p>
      <a
        class="agent-dialog__action"
        :href="agentTelegramUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ $t('header.agent.action') }}
      </a>
    </div>
  </el-dialog>
  </div>
</template>

<script>
import FlyMenuItem from './FlyMenuItem.vue'
import AccountDropdown from './AccountDropdown'
import elementLocale from 'element-ui/lib/locale';
import localeZH from 'element-ui/lib/locale/lang/zh-CN'; 
import localeEN from 'element-ui/lib/locale/lang/en'; 
import { setLang } from '@/http/api/home'
import { cookieAuthEnabled } from '@/http/authMode'
import QrCard from '@/components/qr-card.vue';
import { isPromotionEnabled, promotionConfig, promotionText } from '@/config/site-theme';

export default {
  components: {
    FlyMenuItem,
    AccountDropdown,
    QrCard,
  },
  data() {
    return {
      atTop: true,
      showGoTop: false,
      isHovered: false,
      isQrCardVisible: false,
      isAgentDialogVisible: false,
    }
  },
  props: {
    simple: {
      type: Boolean,
      default: false
    },
    autoHidden: {
      type: Boolean,
      default: false
    },
    showLogin: {
      type: Boolean,
      default: true
    },
    showSignup: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    showHeaderBackground() {
      return !this.atTop || this.isHovered || this.simple;
    },
    showBackground() {
      return this.showHeaderBackground;
    },
    headerClass() {
      return {
        'text-white': this.autoHidden && !this.showHeaderBackground && !this.promotionEnabled,
        '!text-slate-900 bg-white/65 lg:border-b lg:border-slate-900/20 supports-backdrop-blur:bg-white/65': this.showHeaderBackground,
        '!bg-white !text-slate-900 border-0': this.isHovered,
      }
    },
    plans() {
      return this.$t("header.plans")
    },
    regions() {
      return this.$t("home.coverage.regions")
    },
    currentLanguage() {
      return this.$i18n.locale;
    },
    promotionEnabled() {
      return isPromotionEnabled();
    },
    promotionTargetUrl() {
      return promotionConfig.targetUrl;
    },
    agentTelegramUrl() {
      return promotionConfig.agentTelegramUrl;
    },
    promotionLimitedOffer() {
      return promotionText(this, 'limited_offer');
    }
  },
  mounted() {
    this.checkScroll();
    window.addEventListener('scroll', this.checkScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.checkScroll);
  },
  methods: {
    getImg(area) {
      return require(`@/assets/index/img/${area.toLowerCase()}`);
    },
    getRegionImg(area) {
      try {
        return require(`@/assets/index/img/index/img_${area.toLowerCase()}.png`);
      } catch(e) {
        // ignore it
      }
      return null;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      this.atTop = true;
    },
    checkScroll() {
      this.atTop = window.scrollY === 0;
      this.showGoTop = window.scrollY > 1000;
    },
    handleHoverChange(isHovered) {
      this.isHovered = isHovered;
    },
    changeLanguage(lang) {
      if (this.$i18n.locale !== lang) {
        this.$i18n.locale = lang;
        elementLocale.use(this.$i18n.locale === 'zh-cn' ? localeZH : localeEN);
        let oldLang = localStorage.getItem('language');
        localStorage.setItem('language', lang);

        // Cookie mode sends the selected locale with every request.
        if (!cookieAuthEnabled) {
          setLang({lang}).catch(e => { console.log(e) });
        }

        // 手动触发 storage 事件
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'language',
          newValue: lang,
          oldValue: oldLang,
          url: window.location.href
        }));
      }
    },
    handleClick(event) {
      const href = event.currentTarget.href;
      if (href.endsWith("#contacts")) {
        event.preventDefault();
        this.openWechatQr();
      }
    },
    openWechatQr() {
      window.dispatchEvent(new CustomEvent('open-wechat-qr'));
    },
    openAgentDialog() {
      this.isAgentDialogVisible = true;
    },
  },
  filters: {
    formatNumber(value) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style lang="scss" scoped>
  .btn-list a::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: rgba(46,79,237,.12);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.3s ease-in-out;
    z-index: -1;
  }

  .btn-list a:hover::after {
    transform: translate(-50%, -50%) scale(1.2);
  }

  .quick-group {
    z-index: 999999999;
  }

  .qr-card-panel {
    position: absolute;
    left: -20px;                  
    top: 50%;
    transform: translate(-100%, -50%);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.6s ease;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .language-switch {
    display: inline-flex;
    align-items: center;
    height: 28px;
    margin-left: 12px;
    padding: 0 8px;
    border-radius: 999px;
    color: currentColor;
    font-size: 13px;
    line-height: 18px;
    opacity: 0.74;
  }

  .language-switch__item {
    appearance: none;
    border: 0;
    padding: 0 4px;
    background: transparent;
    color: currentColor;
    cursor: pointer;
    font: inherit;
    font-weight: 500;
    opacity: 0.62;
    transition: opacity 0.2s ease, color 0.2s ease;
  }

  .language-switch__item:hover,
  .language-switch__item.is-active {
    opacity: 1;
  }

  .language-switch__item.is-active {
    font-weight: 700;
  }

  .language-switch__divider {
    color: currentColor;
    opacity: 0.3;
    padding: 0 2px;
    user-select: none;
  }

  .summer-header {
    color: #173b3a !important;
    background: rgba(255, 255, 255, 0.74);
    border-bottom: 1px solid rgba(203, 232, 223, 0.92);
    box-shadow: 0 10px 26px rgba(18, 110, 106, 0.08);
    backdrop-filter: blur(12px);
  }

  .summer-header-transparent {
    color: #173b3a !important;
    background: transparent !important;
    border-bottom: 0 !important;
    box-shadow: none !important;
  }

  .summer-nav-promo {
    border-radius: 999px;
    white-space: nowrap;
  }

  .summer-nav-promo__mark {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 7px;
    border-radius: 999px;
    color: #fff;
    background: #ff8a3d;
  }

  .header-menu-promo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 86px;
    height: 24px;
    padding: 0 9px;
    border-radius: 999px;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    background: #ff8a3d;
    box-shadow: 0 8px 18px rgba(255, 138, 61, 0.18);
  }

  .agent-entry {
    appearance: none;
    border: 0;
    background: transparent;
    color: currentColor;
    cursor: pointer;
    font: inherit;
    white-space: nowrap;
  }

  .agent-entry:hover {
    opacity: 0.78;
  }

</style>

<style lang="scss">
  .agent-dialog {
    border-radius: 12px;
  }

  .agent-dialog__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 8px 12px 18px;
    text-align: center;
  }

  .agent-dialog__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    color: #fff;
    font-size: 14px;
    font-weight: 800;
    background: linear-gradient(138deg, #2e6062, #69a971);
    box-shadow: 0 12px 28px rgba(46, 96, 98, 0.2);
  }

  .agent-dialog__body h3 {
    margin: 0;
    color: #173b3a;
    font-size: 22px;
    font-weight: 800;
  }

  .agent-dialog__body p {
    margin: 0;
    color: #5f7472;
    font-size: 14px;
    line-height: 22px;
  }

  .agent-dialog__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 180px;
    height: 42px;
    padding: 0 18px;
    border-radius: 999px;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    background: #ff8a3d;
    text-decoration: none;
  }
</style>
